import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ActionButton from '@applied/dragonfruit/src/components/ActionButton/ActionButton';
import Card from '@applied/dragonfruit/src/components/Card/Card';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import BaseTextField from '@applied/dragonfruit/src/components/TextField/TextField';
import { useForm } from '@tanstack/react-form';
import { updateAuthenticationCookies } from 'api/cookies';
import { z } from 'zod';

import { RevealablePassword } from 'components/common/RevealablePassword/RevealablePassword';
import { type LoginResponse, postLogin } from 'components/LoginPage/login_requests';

import './login-page.scss';

// ===== FORM SCHEMA ===== \\
const loginFormSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type LoginFormData = z.infer<typeof loginFormSchema>;

const defaultValues: LoginFormData = {
  username: '',
  password: '',
};

/**
 * Makes a login request to the backend and updates client-side cookies.
 *
 * @throws If credentials are clearly invalid, login fails, or cookie update fails.
 */
async function handleSubmitRequest(
  credentials: LoginFormData,
  setWarningMessage: (message: string) => void,
  history: ReturnType<typeof useHistory>,
): Promise<void> {
  setWarningMessage('');

  let loginResponse: LoginResponse | null;
  try {
    loginResponse = await postLogin(credentials.username, credentials.password);
  } catch (error) {
    setWarningMessage('Error: Invalid username or password');
    return;
  }

  try {
    if (!loginResponse) {
      throw new Error('Authentication response is not valid');
    }
    updateAuthenticationCookies(loginResponse.authenticationTokens);
    history.push(loginResponse.redirectURL);
  } catch (error) {
    setWarningMessage('Error: Invalid server response');
  }
}

/**
 * The page which allows the user to login.
 */
export function LoginPage(): JSX.Element {
  const [warningMessage, setWarningMessage] = useState('');
  const history = useHistory();

  const form = useForm({
    defaultValues: defaultValues,
    validators: {
      onChange: loginFormSchema,
    },
    onSubmit: ({ value }: { value: LoginFormData }) =>
      handleSubmitRequest(value, setWarningMessage, history),
  });

  useEffect(function updateTabTitle() {
    document.title = 'Applied Intuition - Login';
  }, []);

  return (
    <Flex className="login-page" direction="column" justify="center" alignment="center">
      <Card className="login-card" direction="column" gap="md" padding="xl">
        <Typography className="login-header" variant="title" data-testid="login-header">
          Login
        </Typography>
        <form
          onSubmit={async (event: React.FormEvent) => {
            event.preventDefault();
            await form.handleSubmit();
          }}
        >
          <Flex direction="column" gap="md">
            {/* NOTE: We don't use TextFormField because zod typing with react-form is causing issues;
             * the generic type (FormGenerics) and the generic function type of useForm compute differently.
             * TODO(peter-cardenas-ai): Create a more ergonomic solution.
             */}
            <form.Field name="username">
              {(field) => {
                const isTouched = field.state.meta.isTouched;
                const errors = field.state.meta.errors;
                const errorMessages = errors
                  ?.map((err: unknown) => (err as { message?: string })?.message)
                  .filter(Boolean);
                const errorMessage =
                  errors?.length && isTouched ? errorMessages?.join(', ') : undefined;

                return (
                  <BaseTextField
                    className="username-input"
                    placeholder="Username..."
                    caption="Username"
                    value={field.state.value as string}
                    onChange={(value) => {
                      field.handleChange(value);
                    }}
                    onBlur={field.handleBlur}
                    onSubmit={form.handleSubmit}
                    errorMessage={errorMessage}
                    dataTestId="dragonfruit-text-field-username"
                  />
                );
              }}
            </form.Field>
            <form.Field name="password">
              {(field) => {
                const isTouched = field.state.meta.isTouched;
                const errors = field.state.meta.errors;
                const errorMessages = errors
                  ?.map((err: unknown) => (err as { message?: string })?.message)
                  .filter(Boolean);
                const errorMessage =
                  errors?.length && isTouched ? errorMessages?.join(', ') : undefined;

                return (
                  <RevealablePassword
                    className="password-input"
                    placeholder="Password..."
                    caption="Password"
                    value={field.state.value as string}
                    onChange={(value) => {
                      field.handleChange(value);
                    }}
                    onBlur={field.handleBlur}
                    onSubmit={form.handleSubmit}
                    errorMessage={errorMessage}
                    dataTestId="dragonfruit-password-field-password"
                  />
                );
              }}
            </form.Field>
            <Flex className="submit-login-button-container" direction="column" gap="xs">
              <div>
                <ActionButton
                  className="submit-login-button"
                  appearance="primary"
                  buttonType="submit"
                >
                  Login
                </ActionButton>
              </div>
              {warningMessage !== '' && (
                <Typography className="warning-message" appearance="error" variant="caption">
                  {warningMessage}
                </Typography>
              )}
            </Flex>
          </Flex>
        </form>
      </Card>
    </Flex>
  );
}
